import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { LoggerService } from '../../../../../services/logger-services/logger.service';
import { AgentConfigService } from '../../agent-config/agent-config.service';
import { AgentApiService } from '../../../../../shared/services/agent-api/agent-api.service';
import {
  GetAgentThreadsRequest,
  GetAgentThreadsResponse,
  AgentThread
} from '../../../models';

/**
 * Get Agent Threads Service
 * 
 * Handles retrieval of paginated conversation threads for agents.
 * 
 * Features:
 * - Paginated thread retrieval
 * - Mock mode for development/testing
 * - Configurable via JSON files
 * - Error handling and logging
 */
@Injectable({
  providedIn: 'root'
})
export class GetAgentThreadsService {
  private useMock: boolean = true;
  private baseUrl: string = '/api/agent';
  private endpointPath: string = '/threads';
  private mockResponse: GetAgentThreadsResponse | null = null;

  constructor(
    private logger: LoggerService,
    private agentConfigService: AgentConfigService,
    private agentApiService: AgentApiService
  ) {
    // Load configuration values (AgentConfigService already loaded at app startup)
    this.loadConfigurationValues();
  }

  /**
   * Load configuration values from AgentConfigService
   * 
   * Note: AgentConfigService is already loaded during app initialization,
   * so we just extract the values we need. URL resolution is now handled by AgentApiService.
   */
  private loadConfigurationValues(): void {
    try {
      // 1. Get API config for mock mode
      const apiConfig = this.agentConfigService.getApiConfig() || {};
      this.useMock = apiConfig?.useMock ?? true;
      
      // 2. Keep fallback values for backward compatibility
      this.baseUrl = apiConfig?.baseUrl || '/api/agent';
      this.endpointPath = '/threads';

      // 3. Get mock responses if available
      if (this.useMock) {
        const mockConfig = this.agentConfigService.getMockResponses();
        this.mockResponse = mockConfig?.responses?.get_agent_threads || null;
      }

      // 4. Get the actual endpoint URL from AgentApiService configuration
      const configuredEndpoint = this.agentApiService.getApiUrlFromConfig('get_agent_threads');
      
      this.logger.logInfo(
        `[GetAgentThreads] Configuration loaded - useMock: ${this.useMock}, endpoint: ${configuredEndpoint || `${this.baseUrl}${this.endpointPath}`}`,
        'loadConfigurationValues'
      );
    } catch (error: any) {
      this.logger.logInfo('[GetAgentThreads] Error loading configuration values, using defaults', 'loadConfigurationValues', error);
    }
  }

  /**
   * Get Agent Threads
   * 
   * Retrieves paginated list of conversation threads
   * 
   * @param request - GetAgentThreadsRequest containing filter parameters
   * @returns Observable<GetAgentThreadsResponse>
   */
  getAgentThreads(request: GetAgentThreadsRequest, agentId?: string): Observable<GetAgentThreadsResponse> {
    try {
      this.logger.logInfo(
        `[GetAgentThreads] Getting agent threads for tenant: ${request.tenant_id}, user: ${request.user_id}, agent: ${agentId || 'unknown'}`,
        'getAgentThreads'
      );

      // Validate request
      const validationError = this.validateRequest(request);
      if (validationError) {
        return of(validationError);
      }

      // Check if mock mode is enabled
      if (this.useMock && this.mockResponse) {
        this.logger.logInfo('[GetAgentThreads] Using mock response', 'getAgentThreads');
        return of(this.mockResponse);
      }

      // Make actual API call
      this.logger.logInfo('[GetAgentThreads] Making API call', 'getAgentThreads');
      return this.makeApiCall(request, agentId);
    } catch (error: any) {
      this.logger.logInfo('[GetAgentThreads] Error in getAgentThreads', 'getAgentThreads', error);
      return of(this.createErrorResponse('Internal error occurred', 'INTERNAL_ERROR'));
    }
  }

  /**
   * Make actual API call to backend.
   * Uses AgentApiService with common endpoint fallback (supervisor filter for centralized threads).
   *
   * @param request - GetAgentThreadsRequest
   * @param agentId - Agent identifier
   * @returns Observable<GetAgentThreadsResponse>
   */
  private makeApiCall(request: GetAgentThreadsRequest, agentId?: string): Observable<GetAgentThreadsResponse> {
    try {
      if (agentId) {
        const customBaseUrl = this.agentApiService.getBaseUrlForAgent(agentId);
        if (!customBaseUrl) {
          this.logger.logInfo(
            `[GetAgentThreads] No API base URL for agent "${agentId}" – skipping threads call (avoid localhost)`,
            'makeApiCall'
          );
          return of({
            status: 'success',
            data: { threads: [], pagination: { current_page: 1, total_pages: 1, total_threads: 0 } }
          });
        }
      }

      const endpoint = this.agentApiService.getApiUrlFromConfig('get_agent_threads', agentId) ||
                     `${this.baseUrl}${this.endpointPath}`;

      const requestBody: any = {
        tenant_id: request.tenant_id,
        user_id: request.user_id
      };

      if (request.page !== undefined) {
        requestBody.page = request.page;
      }

      if (request.page_size !== undefined) {
        requestBody.page_size = request.page_size;
      }

      const customBaseUrl = this.agentApiService.getBaseUrlForEndpoint('get_agent_threads', agentId);

      const supervisor = this.agentApiService.getSupervisorForAgent(agentId);
      if (supervisor && supervisor.length > 0) {
        requestBody.supervisor = supervisor;
      }

      if (request.start_date) {
        requestBody.start_date = request.start_date;
      }
      if (request.end_date) {
        requestBody.end_date = request.end_date;
      }

      this.logger.logInfo(
        `[GetAgentThreads] ${agentId || 'default'} – calling ${endpoint} (supervisor: ${JSON.stringify(supervisor || [])})`,
        'makeApiCall'
      );

      return this.agentApiService.post<any>(endpoint, requestBody, undefined, customBaseUrl).pipe(
        map((response: any) => {
          this.logger.logInfo(
            `[GetAgentThreads] API call successful - status: ${response.status}`,
            'makeApiCall'
          );
          return this.mapThreadsResponse(response, request);
        }),
        catchError((error: any) => {
          this.logger.logInfo('[GetAgentThreads] API call failed', 'makeApiCall', error);
          return of(this.handleApiError(error));
        })
      );
    } catch (error: any) {
      this.logger.logInfo('[GetAgentThreads] Error making API call', 'makeApiCall', error);
      return of(this.createErrorResponse('Failed to make API call', 'API_ERROR'));
    }
  }

  /**
   * Map API response to the standard GetAgentThreadsResponse format.
   * Handles both:
   * - Legacy format: { status, data: { threads: [...], pagination: {...} } }
   * - Centralized API format: { status, data: [...], total, page, total_pages, page_size }
   *
   * @param response - Raw API response
   * @param request - Original request (for fallback values)
   * @returns GetAgentThreadsResponse
   */
  private mapThreadsResponse(response: any, request: GetAgentThreadsRequest): GetAgentThreadsResponse {
    if (!response) {
      return this.createErrorResponse('No data returned from API', 'EMPTY_RESPONSE');
    }

    // Legacy format (VIA agent): already has data.threads + data.pagination
    if (response.data?.threads && response.data?.pagination) {
      return response as GetAgentThreadsResponse;
    }

    // Centralized API format: data is a flat array of thread objects
    if (Array.isArray(response.data)) {
      const total: number = response.total ?? response.data.length;
      const page: number = response.page ?? request.page ?? 1;
      const pageSize: number = response.page_size ?? request.page_size ?? 30;

      const threads: AgentThread[] = response.data.map((t: any) => ({
        thread_id: t.thread_id || '',
        thread_name: t.thread_name || '',
        user_id: t.user_id || request.user_id,
        tenant_id: t.tenant_id || request.tenant_id,
        updated_at: t.agent_thread_updated_at || t.updated_at || t.agent_thread_created_at || new Date().toISOString()
      }));

      return {
        status: 'success',
        data: {
          threads,
          pagination: {
            current_page: page,
            total_pages: response.total_pages ?? Math.max(1, Math.ceil(total / pageSize)),
            total_threads: total
          }
        }
      };
    }

    // Fallback: return as-is if it matches expected shape
    return response as GetAgentThreadsResponse;
  }

  /**
   * Validate request payload
   * 
   * @param request - GetAgentThreadsRequest
   * @returns GetAgentThreadsResponse | null (null if valid)
   */
  private validateRequest(request: GetAgentThreadsRequest): GetAgentThreadsResponse | null {
    try {
      // Check required fields
      if (!request.tenant_id || request.tenant_id.trim() === '') {
        return this.createErrorResponse('tenant_id is required', 'VALIDATION_ERROR', {
          tenant_id: 'Field is required'
        });
      }

      if (!request.user_id || request.user_id.trim() === '') {
        return this.createErrorResponse('user_id is required', 'VALIDATION_ERROR', {
          user_id: 'Field is required'
        });
      }

      // Validate optional pagination parameters
      if (request.page !== undefined && (request.page < 1 || !Number.isInteger(request.page))) {
        return this.createErrorResponse('page must be a positive integer', 'VALIDATION_ERROR', {
          page: 'Must be a positive integer'
        });
      }

      if (request.page_size !== undefined) {
        if (!Number.isInteger(request.page_size) || request.page_size < 1 || request.page_size > 1000) {
          return this.createErrorResponse('page_size must be between 1 and 1000', 'VALIDATION_ERROR', {
            page_size: 'Must be between 1 and 1000'
          });
        }
      }

      return null; // Validation passed
    } catch (error: any) {
      this.logger.logInfo('[GetAgentThreads] Error validating request', 'validateRequest', error);
      return this.createErrorResponse('Validation error', 'VALIDATION_ERROR');
    }
  }

  /**
   * Handle API errors
   * 
   * @param error - HTTP error
   * @returns GetAgentThreadsResponse
   */
  private handleApiError(error: any): GetAgentThreadsResponse {
    try {
      if (error.status === 400) {
        return this.createErrorResponse(
          error.error?.message || 'Invalid request parameters',
          'VALIDATION_ERROR',
          error.error?.details
        );
      }

      if (error.status === 401) {
        return this.createErrorResponse('Authentication required', 'UNAUTHORIZED');
      }

      if (error.status === 403) {
        return this.createErrorResponse('Access denied for tenant', 'FORBIDDEN');
      }

      if (error.status === 404) {
        return this.createErrorResponse('Endpoint not found', 'NOT_FOUND');
      }

      if (error.status === 500) {
        return this.createErrorResponse('Internal server error', 'INTERNAL_ERROR');
      }

      return this.createErrorResponse(
        error.error?.message || 'An unexpected error occurred',
        error.error?.code || 'UNKNOWN_ERROR'
      );
    } catch (e: any) {
      this.logger.logInfo('[GetAgentThreads] Error handling API error', 'handleApiError', e);
      return this.createErrorResponse('Failed to handle error', 'ERROR_HANDLING_ERROR');
    }
  }

  /**
   * Create error response
   * 
   * @param message - Error message
   * @param code - Error code
   * @param details - Optional error details
   * @returns GetAgentThreadsResponse
   */
  private createErrorResponse(
    message: string,
    code: string,
    details?: Record<string, string>
  ): GetAgentThreadsResponse {
    const response: GetAgentThreadsResponse = {
      status: 'error',
      message,
      code
    };

    if (details) {
      response.details = details;
    }

    return response;
  }

  /**
   * Check if service is configured to use mock data
   * 
   * @returns boolean
   */
  public isUsingMock(): boolean {
    return this.useMock;
  }

  /**
   * Get current API endpoint
   * 
   * @returns string
   */
  public getEndpoint(): string {
    return this.agentApiService.getApiUrlFromConfig('get_agent_threads') || 
           `${this.baseUrl}${this.endpointPath}`;
  }
}

